import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function CompareDistOdFuzz() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Overdrive vs Distortion vs Fuzz: What’s the Difference?",
      description:
        "Learn the difference between overdrive, distortion, and fuzz, including clipping style, tone, feel, and the kinds of music each effect is best suited for.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage:
        "https://parishaudio.com/learning/compare-distortion-overdrive-fuzz",
      articleSection: "Audio Education",
      keywords: [
        "overdrive vs distortion vs fuzz",
        "overdrive",
        "distortion",
        "fuzz",
        "guitar pedals",
        "audio clipping",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "Overdrive",
      text: "The smoothest and most amp-like of the three, with soft clipping and strong touch response.",
    },
    {
      title: "Distortion",
      text: "More aggressive and saturated, with harder clipping and a more reshaped, compressed tone.",
    },
    {
      title: "Fuzz",
      text: "The wildest option, with extreme clipping, heavy compression, and a buzzy or wooly character.",
    },
  ];

  const comparisonRows = [
    {
      effect: "Overdrive",
      sound: "Warm, dynamic breakup",
      clipping: "Soft clipping",
      bestFor: "Blues, rock, country",
      examples: "Tube Screamer, Klon Centaur",
      link: "/learning/overdrive",
    },
    {
      effect: "Distortion",
      sound: "Aggressive, saturated",
      clipping: "Hard clipping",
      bestFor: "Metal, hard rock, grunge",
      examples: "DS-1, RAT",
      link: "/learning/distortion",
    },
    {
      effect: "Fuzz",
      sound: "Wild, wooly, buzzy",
      clipping: "Extreme clipping",
      bestFor: "Psychedelic, grunge, garage rock",
      examples: "Big Muff, Fuzz Face",
      link: null,
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const innerPanelSx = {
    borderRadius: "16px",
    backgroundColor: style.darkPanel,
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "none",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Overdrive vs Distortion vs Fuzz | Parish Audio Learning"
        description="Learn the difference between overdrive, distortion, and fuzz, including clipping style, tone, feel, and the kinds of music each effect is best suited for."
        canonical="https://parishaudio.com/learning/compare-distortion-overdrive-fuzz"
        schema={articleSchema}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 250, md: 300 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.72) 42%, rgba(8,12,18,0.38) 72%, rgba(8,12,18,0.18) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.1} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2rem", md: "3rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Overdrive vs Distortion vs Fuzz
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "0.98rem", md: "1.05rem" },
                        lineHeight: 1.75,
                        maxWidth: 760,
                      }}
                    >
                      Overdrive, distortion, and fuzz all come from clipping, but
                      they do not sound or feel the same. Understanding how each one
                      behaves helps you choose the right tool for your instrument, your amp,
                      and the tone you want to create.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={outerPanelSx}>
                  <Box sx={{ p: 2 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: style.color4,
                        mb: 0.5,
                        fontSize: "1.05rem",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: style.color3, lineHeight: 1.65 }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GraphicEqRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What Is Clipping?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    At the heart of overdrive, distortion, and fuzz is clipping.
                    Clipping happens when an audio signal exceeds the level a circuit
                    can cleanly reproduce. Instead of continuing to rise smoothly,
                    the waveform is limited, flattened, or reshaped. That change
                    creates new harmonics, more sustain, and the family of sounds
                    we hear as drive, crunch, or saturation.
                  </Typography>

                  <Box
                    component="ul"
                    sx={{
                      pl: 2.5,
                      m: 0,
                      color: style.color3,
                      "& li": { mb: 1.15, lineHeight: 1.75 },
                    }}
                  >
                    <li>
                      <strong style={{ color: style.color4 }}>Soft clipping:</strong>{" "}
                      the peaks are rounded off more gently, which usually sounds
                      smoother and more natural.
                    </li>
                    <li>
                      <strong style={{ color: style.color4 }}>Hard clipping:</strong>{" "}
                      the peaks are chopped off more abruptly, which sounds more
                      aggressive and saturated.
                    </li>
                    <li>
                      <strong style={{ color: style.color4 }}>Extreme clipping:</strong>{" "}
                      the waveform can begin to resemble a square wave, creating the
                      wild, buzzy behavior commonly associated with fuzz.
                    </li>
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    These three effects all use clipping differently, which is why they
                    respond differently under the fingers and sit differently in a mix.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={outerPanelSx}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Overdrive
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Overdrive is the gentlest of the three. It is designed to mimic
                      the natural breakup of a tube amp being pushed without reaching
                      full saturation.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Because it usually uses softer clipping, overdrive tends to keep
                      more of your picking dynamics and the core character of your guitar
                      and amp. Pick lightly and it stays relatively clean. Dig in and it growls.
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.1, lineHeight: 1.7 },
                      }}
                    >
                      <li>Soft clipping with strong touch response</li>
                      <li>Warmth, sustain, and gentle breakup</li>
                      <li>Often used to push an already driven amp</li>
                      <li>Great for blues, classic rock, country, and alt-rock</li>
                    </Box>

                    <Typography sx={{ fontWeight: 700, color: style.color4 }}>
                      Well-known examples
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.1, lineHeight: 1.7 },
                      }}
                    >
                      <li>
                        <a
                          href="https://www.ibanez.com/usa/products/detail/ts808_99.html"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ibanez Tube Screamer
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.boss.info/global/products/sd-1/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          BOSS SD-1 Super Overdrive
                        </a>
                      </li>
                      <li>Klon Centaur</li>
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={outerPanelSx}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Distortion
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Distortion is more aggressive and more obviously processed than
                      overdrive. Instead of feeling like an amp just starting to break up,
                      distortion tends to create a stronger effect with more saturation
                      and compression.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Harder clipping usually means less dynamic variation and more
                      consistency from note to note. That makes distortion useful when
                      you want punch, sustain, and a more forceful tone.
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.1, lineHeight: 1.7 },
                      }}
                    >
                      <li>Hard clipping and more compression</li>
                      <li>Higher gain and stronger saturation</li>
                      <li>A more reshaped, intentional effect sound</li>
                      <li>Common in hard rock, punk, grunge, and metal</li>
                    </Box>

                    <Typography sx={{ fontWeight: 700, color: style.color4 }}>
                      Well-known examples
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.1, lineHeight: 1.7 },
                      }}
                    >
                      <li>
                        <a
                          href="https://www.boss.info/us/products/ds-1/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          BOSS DS-1 Distortion
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://actentertainment.com/rat-2-distortion-pedal/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Pro Co RAT
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.jimdunlop.com/mxr-distortion/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          MXR Distortion+
                        </a>
                      </li>
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={outerPanelSx}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Fuzz
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Fuzz is the most extreme of the three. Instead of simply adding
                      some grit or saturation, it can completely transform the signal
                      into something buzzy, square, thick, and sometimes chaotic.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Fuzz often feels less polished than distortion and more raw or
                      unruly. That is exactly why people love it.
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.1, lineHeight: 1.7 },
                      }}
                    >
                      <li>Extreme clipping with heavy compression</li>
                      <li>Wooly, buzzy, raw, or broken-sounding tones</li>
                      <li>Major harmonic reshaping</li>
                      <li>Popular in grunge, garage rock, stoner rock, and psychedelic music</li>
                    </Box>

                    <Typography sx={{ fontWeight: 700, color: style.color4 }}>
                      Well-known examples
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.1, lineHeight: 1.7 },
                      }}
                    >
                      <li>
                        <a
                          href="https://www.ehx.com/products/big-muff-pi"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Electro-Harmonix Big Muff Pi
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.jimdunlop.com/fuzz-face-distortion/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Fuzz Face
                        </a>
                      </li>
                      <li>Maestro FZ-1 Fuzz-Tone</li>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.75}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Quick Comparison
                    </Typography>
                  </Stack>

                  <Box sx={{ overflowX: "auto" }}>
                    <Table
                      sx={{
                        minWidth: 720,
                        "& .MuiTableCell-root": {
                          borderColor: "rgba(255,255,255,0.08)",
                          color: style.color3,
                          verticalAlign: "top",
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: style.color4, fontWeight: 700 }}>Effect</TableCell>
                          <TableCell sx={{ color: style.color4, fontWeight: 700 }}>Sound</TableCell>
                          <TableCell sx={{ color: style.color4, fontWeight: 700 }}>Clipping Style</TableCell>
                          <TableCell sx={{ color: style.color4, fontWeight: 700 }}>Best For</TableCell>
                          <TableCell sx={{ color: style.color4, fontWeight: 700 }}>Example Pedals</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comparisonRows.map((row) => (
                          <TableRow key={row.effect}>
                            <TableCell sx={{ color: style.color4 }}>
                              {row.link ? (
                                <Box
                                  component={RouterLink}
                                  to={row.link}
                                  sx={{
                                    color: style.color4,
                                    fontWeight: 700,
                                    textDecoration: "none",
                                  }}
                                >
                                  {row.effect}
                                </Box>
                              ) : (
                                <strong>{row.effect}</strong>
                              )}
                            </TableCell>
                            <TableCell>{row.sound}</TableCell>
                            <TableCell>{row.clipping}</TableCell>
                            <TableCell>{row.bestFor}</TableCell>
                            <TableCell>{row.examples}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={outerPanelSx}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Which One Should You Choose?
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Choosing between overdrive, distortion, and fuzz depends on your
                      playing style, your amp, and the kind of tone you are chasing.
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.1, lineHeight: 1.7 },
                      }}
                    >
                      <li>
                        Choose <strong style={{ color: style.color4 }}>overdrive</strong> if you want warmth, touch sensitivity, and an amp-like response.
                      </li>
                      <li>
                        Choose <strong style={{ color: style.color4 }}>distortion</strong> if you want more gain, tighter sustain, and a stronger processed sound.
                      </li>
                      <li>
                        Choose <strong style={{ color: style.color4 }}>fuzz</strong> if you want bold, unruly, vintage, or experimental textures.
                      </li>
                    </Box>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      You also do not have to pick only one. Many classic tones come
                      from stacking gain stages together, such as an overdrive pushing
                      a distortion pedal or a boost driving a fuzz into a loud amp.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={outerPanelSx}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Final Thoughts
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Overdrive, distortion, and fuzz are not just variations of the
                      same idea. They each offer a different musical feel, a different
                      response under the fingers, and a different relationship to your
                      amp and instrument.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      If you understand the clipping style and the way each effect
                      shapes dynamics, sustain, and harmonics, you can make much better
                      choices about which sound belongs in a song.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      The best approach is still to experiment. Try different pedals,
                      different orderings, and different amp settings. The right answer
                      is often the one that makes you play better and inspires the track.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                    Related reading: learn more about{" "}
                    <Box
                      component={RouterLink}
                      to="/learning/overdrive"
                      sx={{ color: style.color4, textDecoration: "none", fontWeight: 700 }}
                    >
                      overdrive
                    </Box>{" "}
                    and{" "}
                    <Box
                      component={RouterLink}
                      to="/learning/distortion"
                      sx={{ color: style.color4, textDecoration: "none", fontWeight: 700 }}
                    >
                      distortion
                    </Box>
                    .
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      component={RouterLink}
                      to="/learning/overdrive"
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: style.accentGreen,
                        "&:hover": {
                          backgroundColor: style.accentGreen,
                          filter: "brightness(1.05)",
                        },
                      }}
                    >
                      Overdrive
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/learning/distortion"
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.10)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.10)",
                        },
                      }}
                    >
                      Distortion
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}
// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'
// import ReactGA from 'react-ga4';
// import { Paper } from '@mui/material';
// import { Container, Typography, Divider, Box, List, ListItem, ListItemText, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
// import WikiCommonsImage from "../../components/WikiCommonsImage/WikiCommonsImage";


// export default function CompareDistOdFuzz(){

//   const { style, setStyle } = useStyle();
//   const [width, setWidth] = useState(window.innerWidth)

// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;

// useEffect(() => {
//   document.title = "Parish Audio | Overdrive, Distortion, Fuzz Differences";
// }, []);

// ReactGA.send({
//     hitType:"pageview",
//     page:"Fuzz, OD, Distortion",
//   })

//   const [height, setHeight] = useState(window.innerHeight)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);




//     return(
//         <Container maxWidth="md" sx={{ py: 6 }}>
//         <h1 style={{marginBottom:'15px'}} ref={topContainer}>Overdrive vs Distortion vs Fuzz: What’s the Difference?</h1>
  
//         <p>
//         When it comes to shaping the sound of your guitar, bass, or even synth, few effects are as influential—or as essential—as overdrive, distortion, and fuzz. These three forms of clipping effects can transform your tone from a gentle breakup to full-blown sonic destruction. But while many musicians use the terms interchangeably, overdrive, distortion, and fuzz each have distinct characteristics. Understanding these differences can help you find the perfect match for your musical style, your amp, and your sound.
//         </p>
//         <p>
//         In this guide, we’ll dive deep into the world of overdrive, distortion, and fuzz, covering what they are, how they sound, how they work, and some iconic pedals that define each category.
//         </p>


//         <Divider sx={{ my: 4 }} />
// <h2>
// What is Clipping?
// </h2>
// <p>
// At the heart of overdrive, distortion, and fuzz is clipping. Clipping occurs when an audio signal exceeds the maximum capacity of a circuit, causing the tops and bottoms of the waveform to "clip" off.
// </p>
// <ul class="a">
//         <li><strong style={{color:'white'}}>Soft clipping </strong>A single-channel effect where modulation occurs uniformly across the audio signal.</li>
//         <li><strong style={{color:'white'}}>Hard clipping </strong>abruptly chops them off, resulting in a harsher sound.</li>
//         <li><strong style={{color:'white'}}>Extreme clipping, </strong>like fuzz, can square off the wave almost entirely, creating aggressive harmonics and textures.</li>   
//       </ul>
//       <p>
//       Each type of effect introduces clipping differently, changing how your guitar, bass, or keyboard sounds and responds.
//       </p>

//       <Divider sx={{ my: 4 }} />
//         <h2>What is Overdrive?</h2>
//         <p>Overdrive is the gentlest of the three. It’s designed to mimic the natural breakup that occurs when a tube amp is pushed hard but hasn't reached full saturation. Soft clipping in overdrive circuits preserves much of the original dynamics and character of your playing.</p>
//         <p>When you pick lightly, the tone remains clean or slightly gritty. When you dig in, it breaks up into a warm, saturated growl. Overdrive is dynamic—it responds to your touch and playing style.</p>
//         <h3>Key Characteristics of Overdrive: </h3>

//         <ul class="a">
//         <li>Soft clipping with dynamic responsiveness</li>
//         <li>Preserves your guitar and amp's natural tone</li>
//         <li>Enhances sustain and adds warmth</li>
//         <li>Sounds best through tube amplifiers but can work with solid-state amps too</li>  
//         <li>Often used to boost an already overdriven amp</li>     
//       </ul>
//       <h3>
//       Classic Overdrive Pedals:
//       </h3>
//       <ul class="a">
//         <li><strong><a href="https://www.ibanez.com/usa/products/detail/ts808_99.html" target="_blank" rel="noopener noreferrer">Ibanez Tube Screamer</a> (TS9, TS808): </strong>Legendary for its smooth mids and gentle clipping.</li>
//         <li><strong><a href="https://www.boss.info/global/products/sd-1/" target="_blank" rel="noopener noreferrer">BOSS SD-1 Super Overdrive: </a></strong>Known for its affordability and reliability.</li>
//         <li><strong>Klon Centaur: </strong>Transparent overdrive that blends clean and dirty signals.</li>  
//       </ul>
//       <h3>Ideal For: </h3>
//       <ul class="a">
//         <li>Blues, classic rock, country, and alt-rock</li>
//         <li>Players who want to enhance their amp’s natural sound</li>
//         <li>Boosting solos without overwhelming the original tone</li>  
//       </ul>
      
//       <div style={{maxWidth:'250px'}}>
//         <WikiCommonsImage width="100%" bottomText="Ibanez Tube Screamer" bottomTextLinkUrl="https://www.ibanez.com/usa/products/detail/ts808_99.html"  wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Ibanez_ts9_tube_screamer.jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/5/5c/Ibanez_ts9_tube_screamer.jpg" alt = "Green Ibanez Tube Screamer overdrive pedal"  title = "Mataresephotos, CC BY 3.0 US <https://creativecommons.org/licenses/by/3.0/us/deed.en>, via Wikimedia Commons" />
//     </div>
//       <Divider sx={{ my: 4 }} />
//     <h2>What is Distortion?</h2>
//     <p>Distortion is a more aggressive effect than overdrive. It uses hard clipping to transform the tone, creating a heavier, more saturated sound. Where overdrive feels like an extension of your amplifier, distortion creates its own distinct voice—and it’s usually much more compressed and saturated.</p>
//     <p>Distortion doesn’t care how hard you pick—your dynamics are more flattened. What you get is consistent gain, aggressive attack, and enhanced sustain.</p>
// <h3>Key Characteristics of Distortion:</h3>
// <ul class="a">
//         <li>Hard clipping that reshapes the sound</li>
//         <li>Adds more gain, sustain, and saturation than overdrive</li>
//         <li>Compresses dynamics for a thicker sound</li>
//         <li>Great for metal, hard rock, punk, and grunge</li>  
//       </ul>
      
//       <h3>Classic Distortion Pedals:</h3>
// <ul class="a">
//         <li><strong><a href="https://www.boss.info/us/products/ds-1/" target="_blank" rel="noopener noreferrer">BOSS DS-1 Distortion: </a></strong>Used by legends like Kurt Cobain and Steve Vai.</li>
//         <li><strong><a href="https://actentertainment.com/rat-2-distortion-pedal/" target="_blank" rel="noopener noreferrer">Pro Co RAT: </a> </strong>Famous for blending distortion, fuzz, and overdrive textures.</li>
//         <li><strong> <a href="https://www.jimdunlop.com/mxr-distortion/" target="_blank" rel="noopener noreferrer">MXR Distortion+: </a> </strong>Favored by Randy Rhoads for its simplicity and punch.</li>
//       </ul>

//       <h3>Modern Standouts:</h3>
// <ul class="a">
//     <li><strong>1981 Inventions DRV: </strong>A boutique distortion with massive headroom.</li>
//         <li><strong>Empress Heavy Menace: </strong>A modern high-gain distortion powerhouse.</li>
//         <li><strong>Retro Mechanical Labs 432k Rev2: </strong>Modular-style distortion with deep control.</li>
//       </ul>

//       <h3>Ideal For:</h3>
// <ul class="a">
//         <li>Metalheads, grunge lovers, hard rock players</li>
//         <li>Lead guitarists looking for creamy sustain</li>
//         <li>Anyone wanting to reshape their entire tone</li>
//       </ul>

//     <Divider sx={{ my: 4 }} />

// <h2>What is Fuzz?</h2>
// <p>Fuzz is the wild child of the trio. It clips the signal so much that the waveform resembles a square wave. Fuzz pedals don't just distort your sound—they obliterate it. The result is a heavy, wooly, buzzy sound that's so compressed it can feel almost broken.</p>
// <p>Unlike overdrive or distortion, fuzz can sound unpredictable, chaotic, and gloriously lo-fi.</p>

// <h3>Key Characteristics of Fuzz:</h3>
// <ul class="a">
//         <li>Extreme clipping with huge compression</li>
//         <li>Wooly, buzzy, saturated tones</li>
//         <li>Changes the harmonic structure of the signal dramatically</li>
//         <li>Can sound glitchy, broken, or chaotic—in a good way</li>
//       </ul>

//       <h3>Classic Fuzz Pedals:</h3>

//       <ul class="a">
//     <li><strong><a href="https://www.ehx.com/products/big-muff-pi" target="_blank" rel="noopener noreferrer">Electro-Harmonix Big Muff Pi</a></strong>Defined the sound of grunge and alternative rock.</li>
//         <li><strong><a href="https://www.jimdunlop.com/fuzz-face-distortion/" target="_blank" rel="noopener noreferrer"> Fuzz Face </a></strong>(Jimi Hendrix's favorite): Smoother germanium fuzz tone.</li>
//         <li><strong>Maestro FZ-1 Fuzz-Tone: </strong>The original fuzz pedal, made famous by (I Can't Get No) Satisfaction.</li>
//       </ul>

// <h3>Modern Fuzz Highlights:</h3>
// <ul class="a">
// <li><strong>Fairfield Circuitry Roger That!:  </strong>A modern experimental fuzz.</li>
//         <li><strong>EarthQuaker Devices Hizumitas:  </strong>A heavy, Big Muff-inspired fuzz.</li>
//         <li><strong>Fender The Pelt Fuzz: </strong>Versatile, modern fuzz with tone sculpting controls.</li>
//       </ul>


// <h3>
// Ideal For:
// </h3>
// <ul class="a">
// <li>Grunge, stoner rock, psychedelic, garage rock</li>
//         <li>Experimental musicians who want a raw, untamed sound</li>
//         <li>Players who want to completely transform their tone</li>
//       </ul>
// <Divider sx={{ my: 4 }} />

// <h2>Quick Comparison: Overdrive vs Distortion vs Fuzz</h2>

// <Table sx={{ color:style.color4, textAlign:'center',mt:1, maxWidth:'95%'}} aria-label="customized table">
//         <TableHead>
//           <TableRow>
//             <TableCell sx={{p:.5, fontSize:'1rem', color:'white', textAlign:'center'}}><strong>Effect</strong></TableCell>
//             <TableCell sx={{p:.5, fontSize:'1rem', color:'white', textAlign:'center'}}><strong>Sound</strong></TableCell>
//             <TableCell sx={{p:.5, fontSize:'1rem', color:'white', textAlign:'center'}}><strong>Clipping Style</strong></TableCell>
//             <TableCell sx={{p:.5, fontSize:'1rem', color:'white', textAlign:'center'}}><strong>Best For</strong></TableCell>
//              <TableCell sx={{p:.5, fontSize:'1rem', color:'white', textAlign:'center'}}><strong>Example Pedals</strong></TableCell>
//           </TableRow>
//         </TableHead>
//         <TableBody>
//             <TableRow>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}><strong><a href="/learning/overdrive">Overdrive</a></strong></TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}>Warm, dynamic breakup</TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}>Soft clipping</TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}>Blues, Rock, Country</TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}><a href="https://www.ibanez.com/usa/products/detail/ts808_99.html" target="_blank" rel="noopener noreferrer">Tube Screamer</a>, Klon Centaur</TableCell>
//             </TableRow>

//             <TableRow>
//               <TableCell align="center" sx={{p:1, fontSize:'.9rem', color:style.color4}}><strong><a href="/learning/distortion">Distortion</a></strong></TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}>Aggressive, saturated</TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}>Hard clipping</TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}>Metal, Hard Rock, Grunge</TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}><a href="https://www.boss.info/us/products/ds-1/" target="_blank" rel="noopener noreferrer">DS-1</a>, <a href="https://actentertainment.com/rat-2-distortion-pedal/" target="_blank" rel="noopener noreferrer">RAT </a> </TableCell>
//             </TableRow>

//             <TableRow>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}><strong>Fuzz</strong></TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}>Wild, wooly, buzzy</TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}>Extreme clipping</TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}>Psychedelic, Grunge, Garage Rock</TableCell>
//               <TableCell align="center" sx={{p:.5, fontSize:'.9rem', color:style.color4}}><a href="https://www.ehx.com/products/big-muff-pi" target="_blank" rel="noopener noreferrer">Big Muff</a>, <a href="https://www.jimdunlop.com/fuzz-face-distortion/" target="_blank" rel="noopener noreferrer"> Fuzz Face </a></TableCell>
//             </TableRow>

//         </TableBody>
//       </Table>

//       <Divider sx={{ my: 4 }} />

// <h2>Which One Should You Choose?</h2>
// <p>Choosing between overdrive, distortion, and fuzz depends on:</p>
// <ul class="a">
// <li>Your <strong>playing style</strong></li>
//         <li>Your <strong>amp</strong></li>
//         <li>The <strong>sound</strong> you are chasing</li>
//       </ul>
// <p>If you want subtle warmth that responds to your playing, start with overdrive.</p>
// <p>If you crave powerful crunch and high-gain lead tones, distortion is your friend.</p>
// <p>If you want to break all the rules and create massive walls of fuzzed-out sound, go for fuzz.</p>
// <p>And remember—you don’t have to pick just one. Many iconic tones come from stacking multiple gain pedals together, creating complex, layered textures that are greater than the sum of their parts.</p>

// <Divider sx={{ my: 4 }} />

// <h2>Final Thoughts</h2>
// <p>Overdrive, distortion, and fuzz are more than just effects—they’re building blocks of modern music. Whether you’re exploring classic blues, punishing metal, woozy shoegaze, or avant-garde sound design, mastering these clipping effects will help you create your own signature tone.</p>
// <p>The world of gain pedals continues to evolve, with brands like <a href="https://www.strymon.net/" target="_blank" rel="noopener noreferrer">Strymon</a>, <a href="https://www.earthquakerdevices.com/" target="_blank" rel="noopener noreferrer">EarthQuaker Devices</a>, <a href="https://jhspedals.info/" target="_blank" rel="noopener noreferrer">JHS</a>, <a href="https://www.wamplerpedals.com/" target="_blank" rel="noopener noreferrer">Wampler</a>, and <a href="https://www.fender.com/" target="_blank" rel="noopener noreferrer">Fender</a> pushing the boundaries every year. So get out there, experiment, and discover the overdrive, distortion, or fuzz that unlocks your perfect sound.</p>  
  
//       </Container>
//     );
//   };
  


